<template>
  <div id="CampaignEditSegmentActions" class="Main-Content">
    <campaign-edit-header v-if="!loading" :campaign="campaign"></campaign-edit-header>
    <Page
      :title="$t('Segment Actions')"
    >
      <LoadingLayout v-if="loading"/>
      <Card v-if="!loading">
        <CardSection>
          <Select
            :label="$t('Action')"
            :options="[
              {
                value: 'enable',
                label: $t('SegmentActions.Enable segments')
              },
              {
                value: 'disable',
                label: $t('SegmentActions.Disable segments')
              },
              {
                value: 'addOffer',
                label: $t('SegmentActions.Add an offer to segments')
              },
              {
                value: 'removeOffer',
                label: $t('SegmentActions.Remove an offer from segments')
              }
            ]"
            v-model="action"
            :placeholder="$t('SegmentActions.Select an Action')"
          />
        </CardSection>
        <CardSection v-if="action === 'addOffer' || action === 'removeOffer'">
          <Select 
            :label="action === 'addOffer' ? $t('SegmentActions.Offer to add') : $t('SegmentActions.Offer to remove')"
            :placeholder="$t('SegmentActions.chooseOfferPlaceholder', [`${offerListOptions.length}`])"
            :options="offerListOptions"
            v-model="selectedOffer"
          />
          <Checkbox
            v-if="action === 'addOffer' || action === 'removeOffer'"
            :label="$t('SegmentActions.Include disabled and run out offers')"
            v-model="showInactiveOffers"
          />
        </CardSection>
        <CardSection>
          <Button type="primary" :disabled="!selectedSegmentCount" @click="submit">{{$t('Apply Changes')}}</Button>
        </CardSection>
      </Card>
      <Card v-if="actionSegments.length">
        <CardSection class="card-section-list-item">
          <div
            @click.prevent="toggleSelectAll"
          >
            <Checkbox
              :label="listSummary"
              :checked="allSelected"
              :indeterminate="selectedSegmentCount > 0 && selectedSegmentCount < actionSegments.length"
            />
          </div>
        </CardSection>
        <div
          v-for="segment in actionSegments"
          :key="segment.id"
          :class="`segment-list-item ${segment.isSelected ? 'is-selected' : ''}`"
          @click.prevent="segment.isSelected = !segment.isSelected"
        >
          <CardSection class="card-section-list-item">
            <span class="card--id">#{{segment.id}}</span>
            <Checkbox 
              :label="segment.name"
              :checked="segment.isSelected"
            />
            <Stats>
              <Stat
                :label="$t('Type')"
                :value="formatSegmentType(segment.type)"
              />
              <Stat
                :label="$t('Status')"
                :value="segment.status === 'active' ? $t('Enabled') : $t('Disabled')"
              />
              <Stat
                :label="$t('Tag')"
                :value="segment.tag || 'N/A'"
              />
              <Stat
                :label="$t('Options')"
                :value="segment.options"
              />
            </Stats>
            <div
              v-for="offerId in segment.offerIds"
              :key="offerId"
            >
              <Icon icon="fas fa-tag"/> {{ formatOfferById(offerId) }}
            </div>
            <div v-if="segment.widget">
              <Icon icon="fas fa-window-maximize"/> {{ segment.widget.name }}
            </div>
          </CardSection>
        </div>
      </Card>
    </Page>
  </div>
</template>

<script>

import CampaignEditHeader from '@/components/CampaignEditHeader'
import OffersUtil from '../classes/utils/OffersUtil'

export default {
  name: 'CampaignEditSegmentActions',
  data () {
    return {
      campaign: null,
      currency: null,
      offers: null,
      segments: null,
      widgets: null,
      action: null,
      actionSegments: [],
      showInactiveOffers: false,
      selectedOffer: null,
      loading: true,
      selectAll: false
    }
  },
  created() {
    if (!this.theUser.isSuper) {
      this.$router.push('/')
    }
    this.$watch(function () {
      return this.action + (this.selectedOffer ? this.selectedOffer.id : null);
    }, function () {
      this.updateActionSegments();
    }, { immediate: true });
  },
  mounted() {
    this.initializeData()
  },
  methods: {
    formatOfferById (id) {
      const offer = this.offers.find(o => o.id === id)
      return offer && offer.label;
    },
    formatSegmentType (type) {
      return this.segmentTypeMap[type];
    },
    isOfferSegment (segment) {
      return segment.type === 'include' || segment.type === 'exit-intent';
    },
    toggleSelectAll() {
      this.selectAll = !this.selectAll
      this.updateActionSegments()
      this.actionSegments.forEach(s => s.isSelected = this.selectAll)
    },
    updateActionSegments() {
      // console.log('update triggered')
      let filtered = []
      if (this.action) {
        if (this.action === 'enable') {
          filtered = this.formattedSegments.filter(s => {
            return s.status === 'disabled'
          })
        } else if (this.action === 'disable') {
          filtered = this.formattedSegments.filter(s => {
            return s.status === 'active'
          })
        } else if (this.action === 'addOffer') {
          filtered = this.selectedOffer ? this.formattedSegments.filter(s => {
            return this.isOfferSegment(s) && s.offerIds.indexOf(this.selectedOffer.id) === -1
          }) : []
        } else if (this.action === 'removeOffer') {
          filtered = this.selectedOffer ? this.formattedSegments.filter(s => {
            return this.isOfferSegment(s) && s.offerIds.indexOf(this.selectedOffer.id) !== -1
          }) : []
        }
      }
      this.actionSegments = filtered
    },
    submit() {
      // console.log('submit')
      const confirm = this.$ui.confirm({
        title: this.$t('Are you sure?'),
        primaryActionLabel: this.$t('Confirm'),
        primaryActionType: 'primary'
      })
      if (!confirm) {
        return
      }
      if (this.action === 'enable') {
        this.api.enableSegments(parseInt(this.$route.params.id), this.selectedSegments.map(s => s.id))
          .catch(this.apiCatch)
      } else if (this.action === 'disable') {
        this.api.disableSegments(parseInt(this.$route.params.id), this.selectedSegments.map(s => s.id))
          .catch(this.apiCatch)
      } else if (this.action === 'addOffer') {
        this.api.insertSegmentOffers(this.selectedSegments.map(s => s.id), this.selectedOffer ? this.selectedOffer.id : null)
          .catch(this.apiCatch)
      } else if (this.action === 'removeOffer') {
        this.api.removeSegmentOffers(parseInt(this.$route.params.id), 
          this.selectedSegments.map(s => s.id), 
          this.selectedOffer ? this.selectedOffer.id : null)
          .catch(this.apiCatch)
      }
    },
    initializeData() {
      this.loading = true
      Promise.all([
        this.api.getCampaign(parseInt(this.$route.params.id)),
        this.api.getSegmentActionData(parseInt(this.$route.params.id))
      ])
        .then((responses) => {
          this.campaign = responses[0]
          this.currency = this.campaign.currency
          this.offers = OffersUtil.fillForFrontend(responses[1].offers)
          this.segments = responses[1].segments
          this.widgets = responses[1].widgets
          this.loading = false
        })
        .catch(this.apiCatch)
    }
  },
  computed: {
    segmentTypeMap() {
      return {
        'display-none': this.$t('Hide Offers'),
        'exclude': this.$t('Do Not Target'),
        'ignore-user': this.$t('Ignore User'),
        'exit-intent': this.$t('Exit Intent'),
        'include': this.$t('Standard')
      }
    },
    offerListOptions() {
      let offers = []
      if (this.action === 'addOffer') {
        offers = this.offers
      } else if (this.actionId === 'removeOffer') {
        // Only return offers that are currently attached to segments.
        const map = {};
        this.formattedSegments.forEach((segment) => {
          segment.offerIds.forEach((id) => {
            if (!map[id]) {
              map[id] = true;
              offers.push(this.offers.find(o => o.id === id))
            }
          });
        });
      }
      if (!this.showInactiveOffers) {
        offers = offers.filter(o => !o.isDisabled && o.status === 'active');
      }
      return offers.map(o => {
        return {
          label: o.label,
          value: o
        }
      })
    },
    allSelected() {
      return this.selectedSegmentCount === this.actionSegments.length
    },
    selectedSegments () {
      return this.actionSegments.filter(s => s.isSelected);
    },
    selectedSegmentCount() {
      return this.selectedSegments.length
    },
    listSummary () {
      return this.$t('SegmentActions.listSummary', [`${this.selectedSegmentCount}`, `${this.actionSegments.length}`]);
    },
    formattedSegments () {
      return this.segments.map(d => {
        const options = [];
        if (d.overrideDnt) {
          options.push(this.$t('Override DNT'));
        }
        if (d.overrideGenius) {
          options.push(this.$t('Override Algorithms'));
        }
        if (d.isUpsell) {
          options.push(this.$t('Upsell'));
        }
        return {
          ...d,
          options: options.length ? options.join(', ') : 'N/A',
          widget: this.widgets.find(w => w.id === d.widgetId),
          isSelected: false
        };
      });
    }
  },
  components: {
    CampaignEditHeader
  }
}

</script>

<style lang="scss">
#CampaignEditSegmentActions {
  .is-selected {
    background-color: #ECEEF7;
  }
  .segment-list-item {
    &:hover {
      background-color: #F5F5F5;
      cursor: pointer;
    }
  }
  .card--id {
    float: right; 
    color: #919eab; 
    margin-top: 8px;
  }
  .card-section-list-item {
    padding: 10px;
    &:hover {
      cursor: pointer;
    }
  }
}
</style>
